import * as React from "react";
import * as ReactDOM from "react-dom";
import {Store, Dispatch} from "redux";
import {Provider, connect} from "react-redux";

import Api, {ApiState} from "./Api";
import StateListFactory from "./utils/StateListFactory";
import {NotificationStateList} from "./definitions/Config";
import BubbleManager, {BubbleManagerProps} from "./components/BubbleManager";
import NotificationCenter, {NotificationCenterProps} from "./components/NotificationCenter";
import NotificationIcon, {NotificationIconProps} from "./components/NotificationIcon";

import MuiThemeProvider from 'material-ui/styles/MuiThemeProvider';
import diyLightTheme = require('diy/material-ui/diyLightTheme');

import {App as ToolbarApp} from 'diy/toolbar/App';
import {Action, actionCreatorFactory} from "diy/redux/typedActions";
import {AppState} from "diy/redux/app/state";
const actionCreator = actionCreatorFactory("diy/toolbar/notification/");

/**
 * Redux Actions to update the API Status (ready / paused)
 * + Thunks to attach a Promise to the Actions
 */
const readyApiAction = actionCreator<void>(
    "readyApi"
);
const pauseApiAction = actionCreator<void>(
    "pauseApi"
);
const resumeApiAction = actionCreator<void>(
    "resumeApi"
);
export const readyApi = () => {
    return (dispatch: Dispatch) => {
        return Promise.resolve(
            dispatch(readyApiAction(void 0))
        );
    };
};
export const pauseApi = () => {
    return (dispatch: Dispatch) => {
        return Promise.resolve(
            dispatch(pauseApiAction(void 0))
        );
    };
};
export const resumeApi = () => {
    return (dispatch: Dispatch) => {
        return Promise.resolve(
            dispatch(resumeApiAction(void 0))
        );
    };
};

/**
 * Redux Action to open / close the Notification Center
 * + Thunk to attach a Promise to the Action
 */
export const openCenterAction = actionCreator<void>(
    "openCenter"
);
export const closeCenterAction = actionCreator<void>(
    "closeCenter"
);
export const openCenter = () => {
    return (dispatch: Dispatch) => {
        return Promise.resolve(
            dispatch(openCenterAction(void 0))
        );
    };
};
export const closeCenter = () => {
    return (dispatch: Dispatch) => {
        return Promise.resolve(
            dispatch(closeCenterAction(void 0))
        );
    };
};

/**
 * Redux Action to add / update a Notification State in the list
 * + Thunk to attach a Promise to the Action
 */
type PushNotificationStatesPayload = {
    notificationStateList: NotificationStateList
};
const pushNotificationStatesAction = actionCreator<PushNotificationStatesPayload>(
    "pushNotificationStates"
);
export const pushNotificationStates = (payload: PushNotificationStatesPayload) => {
    return (dispatch: Dispatch) => {
        return Promise.resolve(
            dispatch(pushNotificationStatesAction(payload))
        );
    };
};

/**
 * Redux Action to update the Notification State list with another list
 * + Thunk to attach a Promise to the Action
 */
type UpdateNotificationStatePayload = {
    notificationStateList: NotificationStateList
};
const updateNotificationStateListAction = actionCreator<UpdateNotificationStatePayload>(
    "updateNotificationStateList"
);
export const updateNotificationStateList = (payload: UpdateNotificationStatePayload) => {
    return (dispatch: Dispatch) => {
        return Promise.resolve(
            dispatch(updateNotificationStateListAction(payload))
        );
    };
};

/**
 * Redux functions for our State management
 * Affected components: NotificationCenter, BubbleManager
 */
export default class ReduxApi {

    static reduce(
        state: ApiState = Api.getInitialState(),
        action: Action<any>
    ): ApiState {

        switch (action.type) {

            /** Pauses the API */
            case pauseApiAction.type:
                return Object.assign({}, state, {
                    isPaused: true
                });

            /** Resumes the API */
            case resumeApiAction.type:
                return Object.assign({}, state, {
                    isPaused: false
                });

            /** Marks the API as ready  */
            case readyApiAction.type:
                return Object.assign({}, state, {
                    isReady: true
                });

            /**
             * Opens the Notification Center and "pauses" the Bubble Manager
             * (all bubbles close)
             */
            case openCenterAction.type:
                return Object.assign({}, state, {
                    isCenterOpen: true
                });

            /**
             * Closes the Notification Center and "restarts" the Bubble Manager
             * (new bubbles can pop again)
             */
            case closeCenterAction.type:
                return Object.assign({}, state, {
                    isCenterOpen: false
                });

            /**
             * Changes the state list (creates one / updates one)
             */
            case pushNotificationStatesAction.type:
                let newNotificationStateList = StateListFactory.push(
                    state.notificationStateList,
                    action.payload.notificationStateList
                );
                let isCenterOpen = (
                    state.isCenterOpen &&
                    (StateListFactory.count(newNotificationStateList) > 0)
                );
                return Object.assign({}, state, {
                    notificationStateList: newNotificationStateList,
                    isCenterOpen: isCenterOpen
                });

            /**
             * Changes the configs (merge two lists)
             * Closes the Notification Center if there is no more notifications to show
             */
            case updateNotificationStateListAction.type:
                return Object.assign({}, state, {
                    notificationStateList: StateListFactory.update(
                        state.notificationStateList,
                        action.payload.notificationStateList
                    )
                });

            default:
                return state;
        }
    }

    /**
     * Renders our components according to the Redux state
     *
     * @param {Store} store
     * @param {ToolbarApp} toolbar
     */
    static render(store: Store, toolbar: ToolbarApp) {
        /** Generating BODY container for the Bubbles and the Notification Center */
        let container = document.createElement("div");
        container.setAttribute("id", "diy-notification");
        document.body.appendChild(container);

        /** Generating the Toolbar container for the Notification icon */
        toolbar.getToolbar().pushToPanel("left", '<div id="diy-notification-icon"></div>', true);
        let iconContainer = document.getElementById("diy-notification-icon");

        /** Connecting React Components */
        const ConnectedBubbleManager = connect(this.mapStateToBubbleManagerProps)(BubbleManager);
        const ConnectedNotificationCenter = connect(this.mapStateToNotificationCenterProps)(NotificationCenter);
        const ConnectedNotificationIcon = connect(this.mapStateToNotificationIconProps)(NotificationIcon);

        /**
         * We have two HTML elements with pointer events in it (Notification Center and Bubbles)
         * that could eventually have different positions in the viewport
         * "pointer-events: none" makes sure you can still click the elements underneath
         */
        ReactDOM.render(
            <Provider store={store}>
                <MuiThemeProvider muiTheme={diyLightTheme}>
                    <div style={{pointerEvents: "none"}}>
                        <ConnectedBubbleManager />
                        <ConnectedNotificationCenter />
                    </div>
                </MuiThemeProvider>
            </Provider>,
            container
        );

        /** Rendering the Notification Icon in the Toolbar's left panel */
        ReactDOM.render(
            <Provider store={store}>
                <MuiThemeProvider muiTheme={diyLightTheme}>
                    <ConnectedNotificationIcon />
                </MuiThemeProvider>
            </Provider>,
            iconContainer
        );
    }

    /**
     * Simple selector to get our state branch for Notifications
     * (some functions need this)
     *
     * @param   {AppState} state
     * @returns {ApiState}
     */
    static select(state: AppState): ApiState {
        return state.notification;
    }

    /**
     * Gets new Bubble Manager props according to the Redux state
     *
     * @param   {ApiState} state
     * @returns {BubbleManagerProps}
     */
    private static mapStateToBubbleManagerProps(state: AppState): BubbleManagerProps {
        return {
            pause: (state.notification.isCenterOpen || state.notification.isPaused),
            notificationStateList: state.notification.notificationStateList
        };
    }

    /**
     * Gets new Notification Center props according to the Redux state
     *
     * @param   {ApiState} state
     * @returns {NotificationCenterProps}
     */
    private static mapStateToNotificationCenterProps(state: AppState): NotificationCenterProps {
        return {
            open: (state.notification.isCenterOpen && !state.notification.isPaused),
            notificationStateList: state.notification.notificationStateList
        };
    }

    /**
     * Gets new Notification Icon props according to the Redux state
     *
     * @param   {ApiState} state
     * @returns {NotificationIconProps}
     */
    private static mapStateToNotificationIconProps(state: AppState): NotificationIconProps {
        return {
            counter: StateListFactory.count(state.notification.notificationStateList, false),
            hasNotifications: StateListFactory.count(state.notification.notificationStateList) > 0,
            active: state.notification.isCenterOpen
        };
    }
}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/toolbar/notificationNG/ReduxApi.tsx
 **/